<template>
  <v-row class="mx-0 my-2 mx-md-2">
    <v-col
      cols="12"
      sm="6"
    >
      <div>
        <span class="font-weight-bold"> {{ $t('settings.crm.empro-filter.title') }}<br /> </span>
        <p class="pa-0 ma-0 text-grey">
          {{ $t('settings.crm.empro-filter.body-texts.0') }}<br />
          {{ $t('settings.crm.empro-filter.body-texts.1') }}
        </p>
        <p class="mt-2 text-grey">
          <b>{{ $t('labels.caution') }}:</b> {{ $t('settings.crm.empro-filter.body-texts.2') }}
          {{ $t('settings.crm.empro-filter.body-texts.3') }}
        </p>
      </div>
    </v-col>
    <v-col
      cols="12"
      offset-md="1"
      md="5"
      class="pt-2 pt-md-8"
    >
      <ContactFilter
        :contact-status="emproContactTypes"
        :initial-selected-contact-types="selectedContactTypes"
        :add-count="addCount"
        :count-is-loading="countIsLoading"
        :hint="$t('settings.crm.empro-filter.labels.hint')"
        :label="$t('settings.crm.empro-filter.labels.label')"
        :is-edit-mode="isEditMode"
        @blur="updateContactTypes"
      />

      <v-alert
        v-if="countIsLoading || selectedContactTypes.length > 0"
        type="info"
        density="compact"
        text
      >
        <span v-if="countIsLoading">
          <v-progress-circular
            :size="20"
            :width="3"
            class="px-4"
            indeterminate
          />
          {{ $t('settings.crm.empro-filter.info-texts.0') }} ...
        </span>
        <div v-else>
          <p>
            {{ $t('settings.crm.empro-filter.body-texts.1', { combinedEmproContactsCount }) }}
          </p>
          <p>
            {{ $t('settings.crm.empro-filter.body-texts.2') }}<br />
            {{ $t('settings.crm.empro-filter.body-texts.3') }}<br /><br />
            {{ $t('settings.crm.empro-filter.info-texts.4', { readablePlatformName }) }}
          </p>
        </div>
      </v-alert>
    </v-col>
  </v-row>
</template>
<script>
import bus, { eventNames } from '@/lib/eventBus.js'
import brandingMixin from '@/mixins/branding.js'
import ContactFilter from '@/modules/settings/crmSettings/ContactFilter.vue'

import GET_EMPRO_CONFIG from './queries/EmproConfig.gql'
import EMPRO_CONTACT_COUNT from './queries/EmproContactCount.gql'
import EMPRO_CONTACT_TYPES from './queries/EmproContactTypes.gql'
import SAVE_EMPRO_SELECTED_CONTACT_STATUSES from './queries/SaveEmproSelectedContactStatuses.gql'

export default {
  components: { ContactFilter },
  mixins: [brandingMixin],
  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      selectedContactTypes: [],
      stopCountPoll: false,
    }
  },
  computed: {
    countIsLoading() {
      return !this.emproContactsCount
    },
    totalCount() {
      return Object.values(this.emproContactsCount?.countPerStatus || []).reduce(
        (acc, cur) => acc + cur,
        0
      )
    },
    combinedEmproContactsCount() {
      if (!this.emproContactsCount?.countPerStatus) return
      const count = Object.entries(this.emproContactsCount?.countPerStatus)
        .filter(
          ([key]) =>
            this.selectedContactTypes.includes(key) || this.selectedContactTypes.includes('ALL')
        )
        .reduce((count, [, value]) => count + value, 0)
      return count
    },
  },
  watch: {
    selectedContactTypes: {
      handler(newValue, oldValue) {
        if (!oldValue.includes('ALL') && newValue.includes('ALL')) {
          this.selectedContactTypes = ['ALL']
        }
        if (oldValue.includes('ALL') && newValue.length > 1) {
          this.selectedContactTypes = this.selectedContactTypes.filter((item) => item !== 'ALL')
        }
      },
      deep: true,
    },
  },
  methods: {
    addCount({ text, value }) {
      if (!value) return { divider: true }
      const count =
        value === 'ALL' ? this.totalCount : this.emproContactsCount?.countPerStatus[value] || 0
      return {
        text: Number.isInteger(this.combinedEmproContactsCount)
          ? `${text} (${count})`
          : `${text} (...)`,
        value,
      }
    },

    async updateContactTypes(selectedTypes) {
      this.selectedContactTypes = selectedTypes
      this.isLoading = true
      try {
        await this.$apollo.mutate({
          mutation: SAVE_EMPRO_SELECTED_CONTACT_STATUSES,
          variables: {
            input: {
              selectedContactStatusIds: this.selectedContactTypes,
            },
          },
        })
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.settings.crm.empro-filter.success'),
        })
      } catch (e) {
        await this.$apollo.queries.emproConfig.refetch()
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.crm.empro-filter.error'),
        })
      } finally {
        this.$emit('isFilterSelected', this.selectedContactTypes.length > 0)
        this.isLoading = false
      }
    },
  },
  apollo: {
    emproContactTypes: {
      query: EMPRO_CONTACT_TYPES,
      update: ({ emproContactTypes }) =>
        emproContactTypes.map(({ id, name }) => ({ text: name, value: id })),
    },
    emproConfig: {
      query: GET_EMPRO_CONFIG,
      update({ emproConfig }) {
        this.selectedContactTypes = emproConfig.selectedContactStatusIds
        this.$emit(
          'isFilterSelected',
          this.selectedContactTypes && this.selectedContactTypes.length > 0
        )
        return emproConfig
      },
    },
    emproContactsCount: {
      query: EMPRO_CONTACT_COUNT,
      variables() {
        return {
          input: {
            selectedContactStatusIds: this.selectedContactTypes,
          },
        }
      },
      skip() {
        return this.stopCountPoll
      },
      result(data) {
        if (data?.data?.emproContactsCount) {
          this.stopCountPoll = true
        }
      },
      pollInterval: 10000,
    },
  },
  emits: ['isFilterSelected'],
}
</script>
